/**
 * SmartInspect Dashboard (Consultant) — sibling of SmartEvaluate / SmartBid /
 * SmartImprove dashboards. Reuses the same page architecture: 5-card summary
 * strip, search + filter bar, and a records table. Access is gated by the
 * shared application-access logic (hasApp) — no SmartInspect-specific security.
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useConsultant } from "./ConsultantLayout";
import { hasApp, APP_SMARTINSPECT } from "../lib/applications";
import {
  siListAssessments, siArchiveAssessment, siRestoreAssessment, siDeleteAssessment,
} from "../lib/smartInspectApi";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import ArchiveDialog from "../components/ArchiveDialog";
import RowActionsMenu from "../components/RowActionsMenu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  ClipboardList, FileEdit, CheckCircle2, FileCheck2,
  Search, Plus, Inbox, Eye, Archive as ArchiveIcon, ArchiveRestore, Trash2,
} from "lucide-react";

const STATUS = {
  draft: { label: "Draft", cls: "bg-[#FEF3C7] text-[#92400E] border border-[#92400E]/20" },
  inputs_complete: { label: "Completed", cls: "bg-[#ECFDF5] text-[#047857] border border-[#047857]/20" },
  finalized: { label: "Finalized", cls: "bg-[#EFF6FF] text-[#025979] border border-[#025979]/20" },
  demo_requested: { label: "Demo Requested", cls: "bg-[#EDE9FE] text-[#5B21B6] border border-[#5B21B6]/20" },
  archived: { label: "Archived", cls: "bg-[#F3F4F6] text-[#6B7280] border border-[#6B7280]/20" },
};
const statusMeta = (s) => STATUS[s] || { label: s || "—", cls: "bg-[#F3F4F6] text-[#6B7280] border border-[#6B7280]/20" };

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
};
const fmtNum = (n) => (n === null || n === undefined || n === "" ? "—" : Number(n).toLocaleString());
const fmtRoi = (n) => (n === null || n === undefined ? "N/A" : `${Number(n).toFixed(1)}%`);

function KpiCard({ icon: Icon, label, value, tone = "default", testid }) {
  const tones = {
    default: "text-[#025979]", amber: "text-[#92400E]", green: "text-[#047857]", violet: "text-[#5B21B6]",
  };
  return (
    <div data-testid={testid} className="rounded-xl border border-[#E5E7EB] bg-white p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide font-semibold text-[#9CA3AF]">
        <Icon className={`w-3.5 h-3.5 ${tones[tone]}`} strokeWidth={1.8} /> {label}
      </div>
      <div className="text-2xl font-bold text-[#111827] mt-1.5">{value}</div>
    </div>
  );
}

export default function SmartInspectWorkspacePage() {
  const user = useConsultant();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [pkg, setPkg] = useState("all");
  const [pendingArchive, setPendingArchive] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const authorized = user && hasApp(user, APP_SMARTINSPECT);
  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  useEffect(() => {
    if (user && !authorized) navigate("/transformation", { replace: true });
  }, [user, authorized, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await siListAssessments();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Failed to load SmartInspect assessments");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (authorized) load(); }, [authorized, load]);

  const handleConfirmArchive = async ({ reason, notes }) => {
    if (!pendingArchive) return;
    try {
      await siArchiveAssessment(pendingArchive.id, { archive_reason: reason, archive_notes: notes });
      toast.success("Assessment archived");
      setPendingArchive(null);
      await load();
    } catch (e) {
      toast.error("Failed to archive assessment");
    }
  };

  const handleRestore = async (row) => {
    setRestoringId(row.id);
    try {
      await siRestoreAssessment(row.id);
      toast.success("Assessment restored");
      await load();
    } catch (e) {
      toast.error("Failed to restore assessment");
    } finally {
      setRestoringId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await siDeleteAssessment(confirmDelete.id);
      toast.success("Assessment deleted");
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Admin role required to delete" : "Failed to delete assessment");
    } finally {
      setDeleting(false);
    }
  };

  const kpis = useMemo(() => {
    const total = rows.length;
    const draft = rows.filter((r) => r.status === "draft").length;
    const completed = rows.filter((r) => r.status === "inputs_complete").length;
    const finalized = rows.filter((r) => r.status === "finalized").length;
    return { total, draft, completed, finalized };
  }, [rows]);

  const packageOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.selected_package).filter(Boolean))), [rows]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((r) => {
      // Archived rows are hidden unless explicitly filtered for (mirrors SmartEvaluate).
      if (status === "archived") { if (r.status !== "archived") return false; }
      else if (r.status === "archived") return false;
      if (term && !(`${r.company_name} ${r.contact_name}`.toLowerCase().includes(term))) return false;
      if (status !== "all" && status !== "archived" && r.status !== status) return false;
      if (pkg !== "all" && r.selected_package !== pkg) return false;
      return true;
    });
  }, [rows, q, status, pkg]);

  if (!user || !authorized) return null;

  const hasAny = rows.length > 0;
  const showEmpty = !loading && filtered.length === 0;

  return (
    <div data-testid="smartinspect-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">SmartInspect Dashboard – Consultant Workspace</h1>
          <p className="text-sm text-[#6B7280] mt-1 max-w-2xl">
            Manage SmartInspect ROI assessments, review customer business cases, and prepare executive ROI reports.
          </p>
        </div>
        <Button
          data-testid="si-new-assessment-btn"
          onClick={() => navigate("/smartintegrations/smartinspect/roi")}
          className="bg-[#025979] hover:bg-[#03738C] text-white"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New ROI Assessment
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="si-kpi-cards">
        <KpiCard testid="si-kpi-total" icon={ClipboardList} label="Total Assessments" value={kpis.total} />
        <KpiCard testid="si-kpi-draft" icon={FileEdit} label="Draft" value={kpis.draft} tone="amber" />
        <KpiCard testid="si-kpi-completed" icon={CheckCircle2} label="Completed" value={kpis.completed} tone="green" />
        <KpiCard testid="si-kpi-finalized" icon={FileCheck2} label="Finalized" value={kpis.finalized} />
      </div>

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="si-filter-bar">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="si-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search company or contact"
            className="w-full h-9 pl-9 pr-3 rounded-md border border-[#E5E7EB] text-sm focus:outline-none focus:ring-2 focus:ring-[#025979]/30"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="si-filter-status" className="w-[150px] h-9"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="inputs_complete">Completed</SelectItem>
            <SelectItem value="finalized">Finalized</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Select value={pkg} onValueChange={setPkg}>
          <SelectTrigger data-testid="si-filter-package" className="w-[160px] h-9"><SelectValue placeholder="Package" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Packages</SelectItem>
            {packageOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-[#9CA3AF] ml-auto">Sorted by Last Updated</span>
      </div>

      {/* Table / empty state */}
      <div className="rounded-xl border border-[#E5E7EB] bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-[#6B7280]" data-testid="si-table-loading">Loading assessments…</div>
        ) : showEmpty ? (
          <div className="p-12 text-center" data-testid="si-empty-state">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#F0F9FF] flex items-center justify-center mb-4">
              <Inbox className="w-7 h-7 text-[#025979]" strokeWidth={1.4} />
            </div>
            <p className="text-sm font-medium text-[#111827]">
              {hasAny ? "No assessments match your filters." : "No SmartInspect ROI assessments yet."}
            </p>
            {!hasAny && (
              <Button
                data-testid="si-empty-create-btn"
                onClick={() => navigate("/smartintegrations/smartinspect/roi")}
                className="mt-4 bg-[#025979] hover:bg-[#03738C] text-white"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Create First Assessment
              </Button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="si-assessments-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-[#9CA3AF] border-b border-[#E5E7EB] bg-[#F9FAFB]">
                <th className="text-left font-semibold px-4 py-2.5">Company</th>
                <th className="text-left font-semibold px-3 py-2.5">Contact</th>
                <th className="text-left font-semibold px-3 py-2.5">Package</th>
                <th className="text-right font-semibold px-3 py-2.5">Operating Locations</th>
                <th className="text-right font-semibold px-3 py-2.5">Annual Inspections</th>
                <th className="text-right font-semibold px-3 py-2.5">ROI %</th>
                <th className="text-left font-semibold px-3 py-2.5">Status</th>
                <th className="text-left font-semibold px-3 py-2.5">Last Updated</th>
                <th className="text-right font-semibold px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const sm = statusMeta(r.status);
                return (
                  <tr
                    key={r.id}
                    data-testid={`si-row-${r.id}`}
                    onClick={() => navigate(`/consultant/smartinspect/${r.id}`)}
                    className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-[#111827]">{r.company_name || "—"}</td>
                    <td className="px-3 py-3 text-[#374151]">{r.contact_name || "—"}</td>
                    <td className="px-3 py-3 text-[#374151]">{r.selected_package || "—"}</td>
                    <td className="px-3 py-3 text-right text-[#374151]">{r.operating_locations != null && r.operating_locations !== "" ? fmtNum(r.operating_locations) : "—"}</td>
                    <td className="px-3 py-3 text-right text-[#374151]">{fmtNum(r.annual_inspections)}</td>
                    <td className="px-3 py-3 text-right font-medium text-[#025979]">{fmtRoi(r.roi_percent)}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${sm.cls}`}>{sm.label}</span>
                    </td>
                    <td className="px-3 py-3 text-[#6B7280]">{fmtDate(r.updated_at)}</td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <RowActionsMenu
                        testId={`si-row-actions-${r.id}`}
                        ariaLabel={`Actions for ${r.company_name || "assessment"}`}
                        actions={[
                          {
                            id: "view",
                            label: "View Details",
                            icon: Eye,
                            testId: `si-action-view-${r.id}`,
                            onClick: () => navigate(`/consultant/smartinspect/${r.id}`),
                          },
                          {
                            id: "restore",
                            label: restoringId === r.id ? "Restoring…" : "Restore",
                            icon: ArchiveRestore,
                            visible: r.status === "archived",
                            disabled: restoringId === r.id,
                            testId: `si-action-restore-${r.id}`,
                            onClick: () => handleRestore(r),
                          },
                          {
                            id: "archive",
                            label: "Archive",
                            icon: ArchiveIcon,
                            visible: r.status !== "archived",
                            testId: `si-action-archive-${r.id}`,
                            onClick: () => setPendingArchive({ id: r.id, company_name: r.company_name }),
                          },
                          {
                            id: "delete",
                            label: "Delete",
                            icon: Trash2,
                            destructive: true,
                            separatorBefore: true,
                            visible: isAdmin,
                            testId: `si-action-delete-${r.id}`,
                            onClick: () => setConfirmDelete({ id: r.id, company_name: r.company_name }),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Admin-only delete confirmation (identical to SmartEvaluate) */}
      <Dialog open={!!confirmDelete} onOpenChange={(open) => !open && !deleting && setConfirmDelete(null)}>
        <DialogContent data-testid="si-delete-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-[#111827]">Delete assessment?</DialogTitle>
            <DialogDescription className="text-sm text-[#6B7280] leading-relaxed">
              Are you sure you want to delete this assessment? This action cannot be undone.
              {confirmDelete?.company_name && (
                <>
                  <br />
                  <span className="block mt-2 text-[#111827] font-medium">{confirmDelete.company_name}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setConfirmDelete(null)} disabled={deleting} data-testid="si-delete-cancel">Cancel</Button>
            <Button type="button" onClick={handleConfirmDelete} disabled={deleting} data-testid="si-delete-confirm" className="bg-[#B91C1C] hover:bg-[#991B1B] text-white">
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ArchiveDialog
        open={!!pendingArchive}
        entityLabel="assessment"
        testIdPrefix="si-archive"
        onClose={() => setPendingArchive(null)}
        onConfirm={handleConfirmArchive}
      />
    </div>
  );
}
